import { useState, useEffect } from "react";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableContainer,
  TextField,
  IconButton,
  TableCell,
  TableRow,
  Button,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  TextareaAutosize,
  DialogActions,
} from "@mui/material";
import { format, addDays, subDays } from "date-fns";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import * as consts from "../constants";
import { IOrderInfo } from "../slices/authSlice";
import { useTranslation } from "react-i18next";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Navigate, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../hooks/redux-hooks";
import {
  getOrdersByDriverId,
  createComment,
  changeOrderBasePaymentMethod,
  changeOrderTipsPaymentMethod,
  changeOrderStatus,
  orderNeedCanceled,
  createOplog,
  updateOrderPriceById,
  updateDriverOrders,
} from "../slices/authSlice";
import {
  showNotification,
  NotificationType,
} from "../slices/notificationSlice";
import { getServiceTypeText } from "../utils/orderUtils";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import driverLicenseImage from "../assets/driverLicense.png";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { commonStyles, responsiveWidth } from "../theme/responsiveStyles";

// 配置 dayjs
dayjs.extend(utc);
dayjs.extend(timezone);

const DRIVER_CANCELED_ORDER_IDS_KEY = "driverCanceledOrderIds";

const getDriverCanceledOrderId = () => {
  const value = localStorage.getItem(DRIVER_CANCELED_ORDER_IDS_KEY);
  if (!value) {
    return null;
  }
  const parsed = Number(value);
  if (Number.isNaN(parsed)) {
    return null;
  }
  return parsed;
};

export function DriverOrdersView() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const driverInfo = useAppSelector((state) => state.auth.driverInfo);
  const orders = useAppSelector((state) => state.auth.driver_orders);

  // 初始化时从 localStorage 获取日期，如果没有则使用当前日期
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const savedDate = localStorage.getItem('driverOrdersSelectedDate');
    return savedDate ? new Date(savedDate) : new Date();
  });
  // const [error, setError] = useState<string | null>(null);

  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<IOrderInfo | null>(null);
  const [comment, setComment] = useState("");
  const [qrCodeDialogOpen, setQrCodeDialogOpen] = useState(false);
  const [selectedQrCode, setSelectedQrCode] = useState("");

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedStatusOrder, setSelectedStatusOrder] =
    useState<IOrderInfo | null>(null);
  const [statusSubmittingOrderId, setStatusSubmittingOrderId] = useState<number | null>(null);
  const [cancelReasonDialogOpen, setCancelReasonDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelRequestOrder, setCancelRequestOrder] = useState<IOrderInfo | null>(null);
  const [driverCanceledOrderId, setDriverCanceledOrderId] = useState<number | null>(
    () => getDriverCanceledOrderId()
  );

  const [priceChangeDialogOpen, setPriceChangeDialogOpen] = useState(false);
  const [priceChangeReason, setPriceChangeReason] = useState("");
  const [pendingPriceChange, setPendingPriceChange] = useState<{
    orderId: number;
    field: "base_fare" | "tips";
    value: number;
    changes: string;
  } | null>(null);

  const [editingPrice, setEditingPrice] = useState<{
    orderId: number;
    field: "base_fare" | "tips";
    value: string;
  } | null>(null);

  const [paymentChangeDialogOpen, setPaymentChangeDialogOpen] = useState(false);
  const [paymentChangeReason, setPaymentChangeReason] = useState("");
  const [pendingPaymentChange, setPendingPaymentChange] = useState<{
    orderId: number;
    field: "base_fare_payment_method" | "tips_payment_method";
    value: number;
    changes: string;
  } | null>(null);
  const [showLicenseImage, setShowLicenseImage] = useState(false);
  const hasRequestedCancel = (orderId: number) => driverCanceledOrderId === orderId;

  // 添加处理函数
  const handleStatusChange = async (order: IOrderInfo, newStatus: number) => {
    if (statusSubmittingOrderId === order.order_id) {
      return;
    }

    if (newStatus === consts.orderStatus.CANCELED) {
      if (hasRequestedCancel(order.order_id)) {
        dispatch(
          showNotification({
            message: t("msgCancelAlreadyRequested"),
            type: NotificationType.Warning,
          })
        );
        return;
      }
      setCancelRequestOrder(order);
      setCancelReason("");
      setCancelReasonDialogOpen(true);
      return;
    } else if (newStatus === consts.orderStatus.COMPLETED) {
      // check time: 送机服务的useTime开始的1小时后，才能选择完成订单。接机服务flight_time后3小时才能点击完成
      const now = dayjs().tz('America/Los_Angeles');
      let canComplete = true;
      let errorMessage = "";
      
      if (order.service_type == consts.serviceType.AIRPORT_DROP_OFF) {
        // 送机服务：useTime 后 1 小时才能完成
        const useTime = dayjs(order.use_time).tz('America/Los_Angeles');
        const minCompleteTime = useTime.add(1, 'hour');
        // console.log(now.format(), useTime.format(), minCompleteTime.format());
        if (now.isBefore(minCompleteTime)) {
          canComplete = false;
          errorMessage = t("errDropoffCompleteTooEarly", {
            time: minCompleteTime.format('YYYY-MM-DD HH:mm')
          });
        }
      } else if (order.service_type == consts.serviceType.AIRPORT_PICK_UP) {
        // 接机服务：flight_time 后 3 小时才能完成
        const flightTime = dayjs(order.flight_time).tz('America/Los_Angeles');
        // const minCompleteTime = flightTime.add(3, 'hour');
        const minCompleteTime = flightTime.add(1, 'hour');
        // console.log(now.format(), flightTime.format(), minCompleteTime.format());
        if (now.isBefore(minCompleteTime)) {
          canComplete = false;
          errorMessage = t("errPickupCompleteTooEarly", {
            time: minCompleteTime.format('YYYY-MM-DD HH:mm')
          });
        }
      }

      if (!canComplete) {
        dispatch(
          showNotification({
            message: errorMessage,
            type: NotificationType.Warning,
          })
        );
        return;
      }

      setSelectedStatusOrder(order);
      setStatusDialogOpen(true);
    } else {
      try {
        setStatusSubmittingOrderId(order.order_id);
        await dispatch(
          changeOrderStatus({ order_id: order.order_id, status: newStatus })
        ).unwrap();
      } finally {
        setStatusSubmittingOrderId(null);
      }
    }
  };

  const handleCancelReasonConfirm = async () => {
    if (
      !driverInfo ||
      !cancelRequestOrder ||
      statusSubmittingOrderId === cancelRequestOrder.order_id
    ) {
      return;
    }
    const trimmedReason = cancelReason.trim();
    const driverUid = Number(driverInfo.uid);
    if (trimmedReason.length < 2) {
      dispatch(
        showNotification({
          message: t("errCancelReasonTooShort"),
          type: NotificationType.Warning,
        })
      );
      return;
    }
    try {
      setStatusSubmittingOrderId(cancelRequestOrder.order_id);
      await dispatch(
        orderNeedCanceled({
          order_id: cancelRequestOrder.order_id,
          user_id: driverUid,
          desc: trimmedReason,
        })
      ).unwrap();

      const nextCanceledOrderId = cancelRequestOrder.order_id;
      setDriverCanceledOrderId(nextCanceledOrderId);
      localStorage.setItem(
        DRIVER_CANCELED_ORDER_IDS_KEY,
        String(nextCanceledOrderId)
      );

      dispatch(
        showNotification({
          message: t("msgCancelRequestSuccess"),
          type: NotificationType.Success,
        })
      );

      setCancelReasonDialogOpen(false);
      setCancelReason("");
      setCancelRequestOrder(null);
    } catch (error) {
      const errObj = error as { message?: string } | string;
      const errMsg = typeof errObj === "string" ? errObj : errObj?.message || "";
      dispatch(
        showNotification({
          message: `${t("msgCancelOrderError")}${errMsg}`,
          type: NotificationType.Error,
        })
      );
    } finally {
      setStatusSubmittingOrderId(null);
    }
  };

  const handleStatusConfirm = async () => {
    if (selectedStatusOrder) {
      if (statusSubmittingOrderId === selectedStatusOrder.order_id) {
        return;
      }
      try {
        setStatusSubmittingOrderId(selectedStatusOrder.order_id);
        await dispatch(
          changeOrderStatus({
            order_id: selectedStatusOrder.order_id,
            status: consts.orderStatus.COMPLETED,
          })
        ).unwrap();
        setStatusDialogOpen(false);
        setSelectedStatusOrder(null);
      } finally {
        setStatusSubmittingOrderId(null);
      }
    }
  };

  useEffect(() => {
    if (driverInfo) {
      const formattedDate = format(selectedDate, "yyyy-MM-dd");
      const driverId = parseInt(driverInfo.uid);
      if (!isNaN(driverId)) {
        dispatch(
          getOrdersByDriverId({ driver_id: driverId, date: formattedDate })
        );
      } else {
        navigate("/driver-login");
      }
    }
  }, [selectedDate]);

  if (!driverInfo) {
    return <Navigate replace to={"/driver-login"} />;
  }

  const handleCommentSubmit = async () => {
    const result = await dispatch(
      createComment({
        order_id: selectedOrder?.order_id,
        customer_id: selectedOrder?.customer_id,
        driver_id: +driverInfo.uid,
        customer_name: selectedOrder?.customer_name,
        driver_name: driverInfo.name,
        desc: comment,
        type: consts.commentType.DRIVER_PROVIDED,
      })
    );

    if (createComment.fulfilled.match(result)) {
      // 成功
      dispatch(
        showNotification({
          message: t("msgComentSuccess"),
          type: NotificationType.Success,
        })
      );

      // 更新本地订单数据，设置评论内容
      if (selectedOrder) {
        const updatedOrders = orders.map((order) =>
          order.order_id === selectedOrder.order_id
            ? { ...order, comment: comment }
            : order
        );

        // 使用 Redux 更新订单列表
        dispatch(updateDriverOrders(updatedOrders));
      }

      // 关闭对话框
      setCommentDialogOpen(false);
      setComment("");
      setSelectedOrder(null);
    } else {
      // 失败
      dispatch(
        showNotification({
          message: t("msgComentFailed"),
          type: NotificationType.Error,
        })
      );
    }
  };

  const handlePrevDay = () => {
    const prev = driverInfo.prev_days;
    const prevDay = subDays(selectedDate, 1);
    // const limitDay = subDays(new Date(), prev);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const limitDay = subDays(today, prev);

    if (prevDay < limitDay) {
      dispatch(
        showNotification({
          message: t("errTimeLimited"),
          type: NotificationType.Error,
        })
      );
      return;
    }
    setSelectedDate(prevDay);
    localStorage.setItem('driverOrdersSelectedDate', prevDay.toISOString());
  };

  const handleNextDay = () => {
    const next = driverInfo.next_days;
    const nextDay = addDays(selectedDate, 1);
    const tomorrow = new Date();
    // tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const limitDay = addDays(tomorrow, next);
    if (nextDay > limitDay) {
      dispatch(
        showNotification({
          message: t("errTimeLimited"),
          type: NotificationType.Error,
        })
      );
      return;
    }
    setSelectedDate(nextDay);
    localStorage.setItem('driverOrdersSelectedDate', nextDay.toISOString());
  };

  // 添加更新支付方式的处理函数
  const handlePaymentMethodChangeClick = async (
    orderId: number,
    field: "base_fare_payment_method" | "tips_payment_method",
    value: number
  ) => {
    const order = orders.find((o) => o.order_id === orderId);
    const oldValue =
      field === "base_fare_payment_method"
        ? order?.base_fare_payment_method
        : order?.tips_payment_method;

    // const changes = `${field} from ${oldValue || 'none'} to ${value}`;
    const oldValueText = oldValue ? paymentTypeMap[oldValue] : "none";
    const newValueText = paymentTypeMap[value as consts.PAYMENT_TYPE];
    let changes = '';
    if (field == "base_fare_payment_method") {
      changes = `基础费用支付方式: ${oldValueText} -> ${newValueText}`;
    } else if (field == "tips_payment_method") {
      changes = `小费支付方式: ${oldValueText} -> ${newValueText}`;
    }
    // const changes = `${field} from ${oldValueText} to ${newValueText}`;

    setPendingPaymentChange({ orderId, field, value, changes });
    setPaymentChangeDialogOpen(true);
  };

  // 添加更新支付方式的处理函数
  const handlePaymentMethodChange = async (
    orderId: number,
    field: "base_fare_payment_method" | "tips_payment_method",
    value: number,
    user_id: number,
    desc: string,
    type: consts.oplogType.DRIVER_PROVIDED,
    changes: string
  ) => {
    if (field == "base_fare_payment_method") {
      dispatch(
        changeOrderBasePaymentMethod({
          order_id: orderId,
          method: value,
          field,
          user_id,
          desc,
          type,
          changes,
        })
      );
    } else {
      dispatch(
        changeOrderTipsPaymentMethod({
          order_id: orderId,
          method: value,
          field,
          user_id,
          desc,
          type,
          changes,
        })
      );
    }
  };

  const handlePaymentChangeConfirm = async () => {
    if (pendingPaymentChange && paymentChangeReason.trim()) {
      // 先创建操作日志
      // const oplogResult = await dispatch(
      //   createOplog({
      //     user_id: +driverInfo.uid,
      //     desc: paymentChangeReason,
      //     field: pendingPaymentChange.field,
      //     order_id: pendingPaymentChange.orderId,
      //     type: consts.oplogType.DRIVER_PROVIDED,
      //     changes: pendingPaymentChange.changes,
      //   })
      // );

      //if (createOplog.fulfilled.match(oplogResult)) {
      // 日志创建成功后，执行支付方式更改
      await handlePaymentMethodChange(
        pendingPaymentChange.orderId,
        pendingPaymentChange.field,
        pendingPaymentChange.value,
        +driverInfo.uid,
        paymentChangeReason,
        consts.oplogType.DRIVER_PROVIDED,
        pendingPaymentChange.changes
      );

      // 清理状态
      setPaymentChangeDialogOpen(false);
      setPaymentChangeReason("");
      setPendingPaymentChange(null);
      //}
    }
  };

  // 添加处理函数
  const handlePriceChange = (
    orderId: number,
    field: "base_fare" | "tips",
    value: string,
    changes: string
  ) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      setPendingPriceChange({ orderId, field, value: numValue, changes });
      setPriceChangeDialogOpen(true);
    }
  };

  const handlePriceChangeConfirm = async () => {
    if (pendingPriceChange && priceChangeReason.trim()) {
      // 先创建操作日志
      // const oplogResult = await dispatch(
      //   createOplog({
      //     user_id: +driverInfo.uid,
      //     desc: priceChangeReason,
      //     field: pendingPriceChange.field,
      //     order_id: pendingPriceChange.orderId,
      //     type: consts.oplogType.DRIVER_PROVIDED,
      //     changes: pendingPriceChange.changes,
      //   })
      // );

      //if (createOplog.fulfilled.match(oplogResult)) {
      // 日志创建成功后，执行价格更改
      const params: {
        order_id: number;
        base_fare?: number;
        tips?: number;
        user_id: number;
        desc: string;
        field: string;
        changes: string;
      } = {
        order_id: pendingPriceChange.orderId,
        user_id: +driverInfo.uid,
        desc: priceChangeReason,
        field: pendingPriceChange.field,
        // order_id: pendingPriceChange.orderId,
        // type: consts.oplogType.DRIVER_PROVIDED,
        changes: pendingPriceChange.changes,
      };
      if (pendingPriceChange.field === "base_fare") {
        params.base_fare = pendingPriceChange.value;
      } else {
        params.tips = pendingPriceChange.value;
      }
      await dispatch(updateOrderPriceById(params));

      // 清理状态
      setPriceChangeDialogOpen(false);
      setPriceChangeReason("");
      setPendingPriceChange(null);
      //}
    }
  };

  const handlePriceInputChange = (
    orderId: number,
    field: "base_fare" | "tips",
    value: string
  ) => {
    setEditingPrice({ orderId, field, value });
  };

  const handlePriceConfirmClick = () => {
    if (!editingPrice) {
      return;
    }
    const oldValue =
      editingPrice.field === "base_fare"
        ? orders.find((o) => o.order_id === editingPrice.orderId)?.base_fare
        : orders.find((o) => o.order_id === editingPrice.orderId)?.tips;

    let changes = "";
    if (editingPrice.field == "base_fare") {
      changes = `基础费用: ${oldValue || 0} -> ${editingPrice.value}`;
    } else if (editingPrice.field == "tips") {
      changes = `小费: ${oldValue || 0} -> ${editingPrice.value}`;
    }
    // const changes = `${editingPrice.field} from ${oldValue || 0} to ${
    //   editingPrice.value
    // }`;

    if (editingPrice) {
      handlePriceChange(
        editingPrice.orderId,
        editingPrice.field,
        editingPrice.value,
        changes
      );
      setEditingPrice(null);
    }
  };

  // 现金，信用卡，记账，支票，zelle,venmo
  // cash, credit card, account billing, check, Zelle, and Venmo.
  const paymentTypeMap = {
    [consts.PAYMENT_TYPE.CREDIT_CARD]: t("creditCard"), //"信用卡",
    [consts.PAYMENT_TYPE.CASH]: t("cash"), //"现金",
    [consts.PAYMENT_TYPE.ZELLE]: "Zelle",
    [consts.PAYMENT_TYPE.VENMO]: "Venmo",
    [consts.PAYMENT_TYPE.ACCOUNT_BILLING]: t("accountBilling"), //"记账",
    [consts.PAYMENT_TYPE.CHECK]: t("labelCheck"), //"支票",
  };

  const headerStyle = {
    ...commonStyles.headerCell,
  };

  const cellStyle = {
    ...commonStyles.contentCell,
  };

  const multiLineCellStyle = {
    ...commonStyles.multiLineCell,
  };

  return (
    <>
      <Box sx={{ mb: 5 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            alignItems: { xs: "flex-start", sm: "center" },
            p: 2,
            backgroundColor: "background.paper",
            borderRadius: 1,
            boxShadow: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
            <Box
              sx={{
                typography: "h6",
                fontWeight: "bold",
              }}
            >
              {t("labelCurOrderList")}
            </Box>
          </Box>

          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            gap: 2, 
            width: "100%",
            overflowX: "auto",
            pb: 1
          }}>
            <IconButton
              onClick={() => setShowLicenseImage(!showLicenseImage)}
              sx={{ color: "primary.main" }}
            >
              {showLicenseImage ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
            </IconButton>
            <IconButton
              onClick={handlePrevDay}
              disabled={
                selectedDate <= subDays(new Date(), driverInfo.prev_days)
              }
            >
              <ArrowBackIosIcon />
            </IconButton>
            <TextField
              type="date"
              value={format(selectedDate, "yyyy-MM-dd")}
              onChange={(e) => {
                const newDate = new Date(e.target.value);
                setSelectedDate(newDate);
                localStorage.setItem('driverOrdersSelectedDate', newDate.toISOString());
              }}
              sx={{ width: { xs: 150, sm: 200 } }}
            />
            <IconButton
              onClick={handleNextDay}
              disabled={
                selectedDate >=
                addDays(
                  (() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return today;
                  })(),
                  driverInfo.next_days
                )
              }
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>
        </Box>
        {showLicenseImage && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: "background.paper",
              borderRadius: 1,
              boxShadow: 1,
              textAlign: "left",
            }}
          >
            <Box
              component="img"
              src={driverLicenseImage}
              alt="Driver License"
              sx={{ 
                maxWidth: "100%",
                maxHeight: { xs: "200px", sm: "300px" }
              }}
            />
            <Box
              sx={{
                typography: "h6",
                fontWeight: "bold",
                paddingLeft: { xs: "20px", sm: "200px" },
              }}
            >
              Driver:{driverInfo.name}
            </Box>
          </Box>
        )}
      </Box>

      <Card>
        <Box sx={{ overflowX: "auto" }}>
          <TableContainer>
            {orders.length > 0 ? (
              <Table
                sx={{
                  borderCollapse: "separate",
                  borderSpacing: "0",
                  tableLayout: "fixed",
                  width:
                    orders.length > 0
                      ? `calc(60px + ${orders.length * 150}px)`
                      : "350px",
                  "& tr:hover td": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                  "& tr:hover td:first-of-type": {
                    backgroundColor: "#eeeeee",
                  },
                  "& th, & td": {
                    maxWidth: { xs: "120px", sm: "200px" },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <TableBody>
                  <TableRow>
                    <TableCell
                      sx={{
                        ...headerStyle,
                        width: { xs: "80px", sm: "120px" },
                        maxWidth: { xs: "80px", sm: "150px" },
                      }}
                    >
                      {t("orderId")}
                    </TableCell>
                    {orders.map((order) => (
                      <TableCell
                        key={order.order_id}
                        sx={{
                          ...cellStyle,
                          color: "#1976d2",
                          width: { xs: "100px", sm: "150px" },
                          maxWidth: { xs: "100px", sm: "200px" },
                        }}
                      >
                        {order.order_id}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={headerStyle}>{t("status")}</TableCell>
                    {orders.map((order) => (
                      <TableCell
                        key={order.order_id}
                        sx={{
                          ...cellStyle,
                          bgcolor:
                            order.status === consts.orderStatus.CANCELED
                              ? "#ffebee"
                              : "inherit",
                        }}
                      >
                        <Select
                          size="small"
                          value={order.status || ""}
                          onChange={(e) =>
                            handleStatusChange(order, e.target.value as number)
                          }
                          disabled={
                            order.status === consts.orderStatus.COMPLETED ||
                            order.status === consts.orderStatus.CANCELED ||
                            statusSubmittingOrderId === order.order_id
                          }
                          sx={{ minWidth: { xs: 80, sm: 120 } }}
                        >
                          <MenuItem value={consts.orderStatus.ASSIGNED}>
                            {t("assigned")}
                          </MenuItem>
                          <MenuItem value={consts.orderStatus.COMPLETED}>
                            {t("completed")}
                          </MenuItem>
                          <MenuItem
                            value={consts.orderStatus.CANCELED}
                            disabled={hasRequestedCancel(order.order_id)}
                            // onClick={(e) => {
                            //   e.stopPropagation(); // 阻止事件冒泡
                            //   dispatch(
                            //     showNotification({
                            //       message: t("errPermissionDenied"),
                            //       type: NotificationType.Warning,
                            //     })
                            //   );
                            //   // 防止选择此选项
                            //   const selectElement =
                            //     e.currentTarget.parentElement?.parentElement;
                            //   if (selectElement) {
                            //     // 关闭下拉菜单
                            //     setTimeout(() => {
                            //       (selectElement as HTMLElement).click();
                            //     }, 100);
                            //   }
                            // }}
                            // sx={{ color: "rgba(0, 0, 0, 0.38)" }} // 使文本显示为禁用状态
                          >
                            {t("canceled")}
                          </MenuItem>
                        </Select>
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={headerStyle}>{t("serviceType")}</TableCell>
                    {orders.map((order) => (
                      <TableCell
                        key={order.order_id}
                        sx={{
                          ...cellStyle,
                          color:
                            order.service_type ===
                            consts.serviceType.AIRPORT_PICK_UP
                              ? "#1976d2"
                              : "#f44336",
                        }}
                      >
                        {getServiceTypeText(order.service_type, t)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={headerStyle}>{t("name")}</TableCell>
                    {orders.map((order) => (
                      <TableCell key={order.order_id} sx={cellStyle}>
                        {order.customer_name}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={headerStyle}>{t("phone")}</TableCell>
                    {orders.map((order) => (
                      <TableCell key={order.order_id} sx={cellStyle}>
                        <Box
                          component="a"
                          href={`tel:${order.phone}`}
                          sx={{
                            color: "#1976d2",
                            textDecoration: "none",
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {order.phone}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                  {/* <TableRow>
                    <TableCell sx={headerStyle}>{t("useTime")}</TableCell>
                    {orders.map((order) => (
                      <TableCell
                        key={order.order_id}
                        sx={{
                          ...cellStyle,
                          ...(order.use_time &&
                            order.service_type !==
                              consts.serviceType.AIRPORT_PICK_UP && {
                              bgcolor:
                                new Date(order.use_time).getHours() < 12
                                  ? "#e8f5e9"
                                  : "#fff3e0",
                            }),
                        }}
                      >
                        {order.service_type ===
                        consts.serviceType.AIRPORT_PICK_UP
                          ? "--"
                          : order.use_time
                          ? format(new Date(order.use_time), "yyyy-MM-dd HH:mm")
                          : "--"}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={headerStyle}>{t("flightTime")}</TableCell>
                    {orders.map((order) => (
                      <TableCell
                        key={order.order_id}
                        sx={{
                          ...cellStyle,
                          ...(order.flight_time && {
                            bgcolor:
                              new Date(order.flight_time).getHours() < 12
                                ? "#e8f5e9"
                                : "#fff3e0",
                          }),
                        }}
                      >
                        {order.flight_time
                          ? format(
                              new Date(order.flight_time),
                              "yyyy-MM-dd HH:mm"
                            )
                          : "--"}
                      </TableCell>
                    ))}
                  </TableRow> */}
                  <TableRow>
                    <TableCell sx={headerStyle}>{t("useTimeInDriver")}</TableCell>
                    {orders.map((order) => (
                      <TableCell
                        key={order.order_id}
                        sx={{
                          ...cellStyle,
                          ...(order.use_time &&
                            order.service_type !==
                              consts.serviceType.AIRPORT_PICK_UP && {
                              bgcolor:
                                dayjs(order.use_time)
                                  .tz("America/Los_Angeles")
                                  .hour() < 12
                                  ? "#e8f5e9"
                                  : "#fff3e0",
                            }),
                        }}
                      >
                        {order.service_type ===
                        consts.serviceType.AIRPORT_PICK_UP
                          ? "--"
                          : order.use_time
                          ? dayjs(order.use_time)
                              .tz("America/Los_Angeles")
                              .format("YYYY-MM-DD HH:mm")
                          : "--"}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={headerStyle}>{t("flightTimeInDriver")}</TableCell>
                    {orders.map((order) => (
                      <TableCell
                        key={order.order_id}
                        sx={{
                          ...cellStyle,
                          ...(order.flight_time && {
                            bgcolor:
                              dayjs(order.flight_time)
                                .tz("America/Los_Angeles")
                                .hour() < 12
                                ? "#e8f5e9"
                                : "#fff3e0",
                          }),
                        }}
                      >
                        {order.flight_time
                          ? dayjs(order.flight_time)
                              .tz("America/Los_Angeles")
                              .format("YYYY-MM-DD HH:mm")
                          : "--"}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={headerStyle}>{t("flightNumber")}</TableCell>
                    {orders.map((order) => (
                      <TableCell key={order.order_id} sx={cellStyle}>
                        {order.flight_number ? (
                          <Box
                            component="a"
                            href={`https://www.google.com/search?q=${encodeURIComponent(
                              order.flight_number
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: "#1976d2",
                              textDecoration: "none",
                              "&:hover": {
                                textDecoration: "underline",
                              },
                            }}
                          >
                            {order.flight_number}
                          </Box>
                        ) : (
                          "--"
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={headerStyle}>{t("airport")}</TableCell>
                    {orders.map((order) => (
                      <TableCell
                        key={order.order_id}
                        sx={{
                          ...cellStyle,
                          ...(order.airport &&
                            !order.airport.startsWith("LAX") && {
                              bgcolor: "yellow",
                            }),
                        }}
                      >
                        {order.airport}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={headerStyle}>{t("lobbyService")}</TableCell>
                    {orders.map((order) => (
                      <TableCell key={order.order_id} sx={cellStyle}>
                        {order.lobby_service ? (
                          <Box component="span" sx={{ color: "red" }}>
                            {t("yes")}
                          </Box>
                        ) : (
                          t("no")
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={headerStyle}>{t("labelAddress")}1</TableCell>
                    {orders.map((order) => (
                      <TableCell key={order.order_id} sx={multiLineCellStyle}>
                        {order.addr1 ? (
                          <Box
                            component="a"
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                              order.addr1
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: "#1976d2",
                              textDecoration: "none",
                              "&:hover": {
                                textDecoration: "underline",
                              },
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            }}
                          >
                            {order.addr1}
                          </Box>
                        ) : (
                          "--"
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={headerStyle}>{t("labelAddress")}2</TableCell>
                    {orders.map((order) => (
                      <TableCell key={order.order_id} sx={multiLineCellStyle}>
                        {order.addr2 ? (
                          <Box
                            component="a"
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                              order.addr2
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: "#1976d2",
                              textDecoration: "none",
                              "&:hover": {
                                textDecoration: "underline",
                              },
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            }}
                          >
                            {order.addr2}
                          </Box>
                        ) : (
                          "--"
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={headerStyle}>{t("labelAddress")}3</TableCell>
                    {orders.map((order) => (
                      <TableCell key={order.order_id} sx={multiLineCellStyle}>
                        {order.addr3 ? (
                          <Box
                            component="a"
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                              order.addr3
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: "#1976d2",
                              textDecoration: "none",
                              "&:hover": {
                                textDecoration: "underline",
                              },
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            }}
                          >
                            {order.addr3}
                          </Box>
                        ) : (
                          "--"
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={headerStyle}>{t("labelLuggages")}</TableCell>
                    {orders.map((order) => (
                      <TableCell key={order.order_id} sx={cellStyle}>
                        {order.luggages ?? 0}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={headerStyle}>{t("labelCarryOn")}</TableCell>
                    {orders.map((order) => (
                      <TableCell key={order.order_id} sx={cellStyle}>
                        {order.carry_on ?? 0}
                      </TableCell>
                    ))}
                  </TableRow>

                  <TableRow>
                    <TableCell sx={headerStyle}>
                      {t("labelBaseFarePaymentWay")}
                    </TableCell>
                    {orders.map((order) => (
                      <TableCell key={order.order_id} sx={cellStyle}>
                        <Select
                          size="small"
                          value={order.base_fare_payment_method || ""}
                          onChange={(e) =>
                            handlePaymentMethodChangeClick(
                              order.order_id,
                              "base_fare_payment_method",
                              e.target.value as number
                            )
                          }
                          sx={{ minWidth: { xs: 80, sm: 120 } }}
                        >
                          {Object.entries(paymentTypeMap).map(
                            ([value, label]) => (
                              <MenuItem key={value} value={Number(value)}>
                                {label}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={headerStyle}>{t("baseFare")}</TableCell>
                    {orders.map((order) => (
                      <TableCell key={order.order_id} sx={cellStyle}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            justifyContent: "flex-end",
                          }}
                        >
                          <TextField
                            size="small"
                            type="number"
                            value={
                              editingPrice?.orderId === order.order_id &&
                              editingPrice.field === "base_fare"
                                ? editingPrice.value
                                : order.base_fare || ""
                            }
                            onChange={(e) =>
                              handlePriceInputChange(
                                order.order_id,
                                "base_fare",
                                e.target.value
                              )
                            }
                            inputProps={{ min: 0, step: "0.01" }}
                            sx={{ width: 100 }}
                          />
                          {editingPrice?.orderId === order.order_id &&
                            editingPrice.field === "base_fare" && (
                              <>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={handlePriceConfirmClick}
                                >
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setEditingPrice(null)}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>

                  <TableRow>
                    <TableCell sx={headerStyle}>
                      {t("labelTipsPaymentWay")}
                    </TableCell>
                    {orders.map((order) => (
                      <TableCell key={order.order_id} sx={cellStyle}>
                        <Select
                          size="small"
                          value={order.tips_payment_method || ""}
                          onChange={(e) =>
                            handlePaymentMethodChangeClick(
                              order.order_id,
                              "tips_payment_method",
                              e.target.value as number
                            )
                          }
                          sx={{ minWidth: 120 }}
                        >
                          {Object.entries(paymentTypeMap).map(
                            ([value, label]) => (
                              <MenuItem key={value} value={Number(value)}>
                                {label}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={headerStyle}>{t("tips")}</TableCell>
                    {orders.map((order) => (
                      <TableCell key={order.order_id} sx={cellStyle}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            justifyContent: "flex-end",
                          }}
                        >
                          <TextField
                            size="small"
                            type="number"
                            value={
                              editingPrice?.orderId === order.order_id &&
                              editingPrice.field === "tips"
                                ? editingPrice.value
                                : order.tips || ""
                            }
                            onChange={(e) =>
                              handlePriceInputChange(
                                order.order_id,
                                "tips",
                                e.target.value
                              )
                            }
                            inputProps={{ min: 0, step: "0.01" }}
                            sx={{ width: 100 }}
                          />
                          {editingPrice?.orderId === order.order_id &&
                            editingPrice.field === "tips" && (
                              <>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={handlePriceConfirmClick}
                                >
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setEditingPrice(null)}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={headerStyle}>{t("note")}</TableCell>
                    {orders.map((order) => (
                      <TableCell key={order.order_id} sx={multiLineCellStyle}>
                        {order.notes || "--"}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={headerStyle}>{t("labelComment")}</TableCell>
                    {orders.map((order) => (
                      <TableCell key={order.order_id} sx={cellStyle}>
                        {order.comment ? (
                          <div style={{ whiteSpace: "pre-wrap" }}>
                            {order.comment}
                          </div>
                        ) : (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                              setSelectedOrder(order);
                              setCommentDialogOpen(true);
                            }}
                          >
                            {t("btnAddComment")}
                          </Button>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={headerStyle}>{t("btnQrCode")}</TableCell>
                    {orders.map((order) => (
                      <TableCell key={order.order_id} sx={cellStyle}>
                        {order.qrcode ? (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setSelectedQrCode(order.qrcode || "");
                              setQrCodeDialogOpen(true);
                            }}
                          >
                            {t("btnQrCode")}
                          </Button>
                        ) : (
                          "--"
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            ) : (
              <Box
                sx={{
                  p: 3,
                  textAlign: "center",
                  color: "text.secondary",
                  fontSize: "1rem",
                }}
              >
                {t("labelNoDayOrders")}
              </Box>
            )}
          </TableContainer>
        </Box>
      </Card>
      <Dialog
        open={commentDialogOpen}
        onClose={() => {
          setCommentDialogOpen(false);
          setComment("");
          setSelectedOrder(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("addComment")}</DialogTitle>
        <DialogContent>
          <TextareaAutosize
            minRows={4}
            placeholder={t("labelCommentHolder")}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            style={{
              width: "100%",
              marginTop: "8px",
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid rgba(0, 0, 0, 0.23)",
              fontFamily: "inherit",
              fontSize: "inherit",
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCommentDialogOpen(false);
              setComment("");
              setSelectedOrder(null);
            }}
          >
            {t("btnCancel")}
          </Button>
          <Button
            onClick={handleCommentSubmit}
            variant="contained"
            disabled={!comment.trim()}
          >
            {t("btnSubmit")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={qrCodeDialogOpen}
        onClose={() => {
          setQrCodeDialogOpen(false);
          setSelectedQrCode("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("btnQrCode")}</DialogTitle>
        <DialogContent>
          {selectedQrCode ? (
            <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
              <Box
                component="img"
                src={selectedQrCode}
                alt={t("btnQrCode")}
                sx={{
                  maxWidth: "100%",
                  maxHeight: 420,
                  objectFit: "contain",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              />
            </Box>
          ) : (
            <Box sx={{ mt: 1, textAlign: "center", color: "text.secondary" }}>
              {t("labelNoData")}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedQrCode && (
            <Button
              onClick={() => window.open(selectedQrCode, "_blank", "noopener,noreferrer")}
            >
              {t("btnOpenInNewTab")}
            </Button>
          )}
          <Button
            onClick={() => {
              setQrCodeDialogOpen(false);
              setSelectedQrCode("");
            }}
          >
            {t("btnClose")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={statusDialogOpen}
        onClose={() => {
          setStatusDialogOpen(false);
          setSelectedStatusOrder(null);
        }}
      >
        <DialogTitle>{t("confirmTitle")}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>{t("msgConfirmStatusChange")}</Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setStatusDialogOpen(false);
              setSelectedStatusOrder(null);
            }}
            disabled={
              !!selectedStatusOrder &&
              statusSubmittingOrderId === selectedStatusOrder.order_id
            }
          >
            {t("btnCancel")}
          </Button>
          <Button
            onClick={handleStatusConfirm}
            variant="contained"
            disabled={
              !!selectedStatusOrder &&
              statusSubmittingOrderId === selectedStatusOrder.order_id
            }
          >
            {t("btnConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={cancelReasonDialogOpen}
        onClose={() => {
          setCancelReasonDialogOpen(false);
          setCancelReason("");
          setCancelRequestOrder(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("titleCancelRequest")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("labelCancelReason")}
            type="text"
            fullWidth
            multiline
            rows={4}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCancelReasonDialogOpen(false);
              setCancelReason("");
              setCancelRequestOrder(null);
            }}
            disabled={
              !!cancelRequestOrder &&
              statusSubmittingOrderId === cancelRequestOrder.order_id
            }
          >
            {t("btnCancel")}
          </Button>
          <Button
            onClick={handleCancelReasonConfirm}
            variant="contained"
            disabled={
              !cancelReason.trim() ||
              (!!cancelRequestOrder &&
                statusSubmittingOrderId === cancelRequestOrder.order_id)
            }
          >
            {t("btnConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={paymentChangeDialogOpen}
        onClose={() => {
          setPaymentChangeDialogOpen(false);
          setPaymentChangeReason("");
          setPendingPaymentChange(null);
        }}
        PaperProps={{
          sx: {
            minWidth: 300,
          },
        }}
        // slotProps={{
        //   paper: {
        //     sx: {
        //       minWidth: 400,
        //     },
        //   },
        // }}
      >
        <DialogTitle>{t("titleChangePaymentMethod")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("labelChangeReason")}
            type="text"
            fullWidth
            multiline
            rows={4}
            value={paymentChangeReason}
            onChange={(e) => setPaymentChangeReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPaymentChangeDialogOpen(false);
              setPaymentChangeReason("");
              setPendingPaymentChange(null);
            }}
          >
            {t("btnCancel")}
          </Button>
          <Button
            onClick={handlePaymentChangeConfirm}
            variant="contained"
            disabled={!paymentChangeReason.trim()}
          >
            {t("btnConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={priceChangeDialogOpen}
        onClose={() => {
          setPriceChangeDialogOpen(false);
          setPriceChangeReason("");
          setPendingPriceChange(null);
        }}
        PaperProps={{
          sx: {
            minWidth: 300,
          },
        }}
        // slotProps={{
        //   paper: {
        //     sx: {
        //       minWidth: 300,
        //     },
        //   },
        // }}
      >
        <DialogTitle>{t("titleChangePriceMethod")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("labelChangeReason")}
            type="text"
            fullWidth
            multiline
            rows={4}
            value={priceChangeReason}
            onChange={(e) => setPriceChangeReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPriceChangeDialogOpen(false);
              setPriceChangeReason("");
              setPendingPriceChange(null);
            }}
          >
            {t("btnCancel")}
          </Button>
          <Button
            onClick={handlePriceChangeConfirm}
            variant="contained"
            disabled={!priceChangeReason.trim()}
          >
            {t("btnConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
